import { z } from '../zod';

export const GROUP_REGISTRATION_WHEN = ['checkout', 'afterBooking'] as const;
export const GUEST_DESCRIPTION_MAX_LENGTH = 120;
export const GUEST_SAVE_BUTTON_MAX_LENGTH = 40;
export const GROUP_REGISTRATION_EMAIL_DOMAINS = [
  'any',
  'flag',
  'work',
  'workFlag',
  'same',
] as const;
// enabledAt is set by the API when the setting is switched on, never by the client.
export const GroupRegistrationSettingsInputValidationSchema = z
  .object({
    enabled: z.boolean(),
    when: z.enum(GROUP_REGISTRATION_WHEN),
    requireAllBeforePayment: z.boolean(),
    emailDomains: z.enum(GROUP_REGISTRATION_EMAIL_DOMAINS),
    sendReminders: z.boolean(),
    heading: z.string().max(200),
    description: z.string().max(GUEST_DESCRIPTION_MAX_LENGTH),
    saveButton: z.string().max(GUEST_SAVE_BUTTON_MAX_LENGTH),
  })
  .partial()
  .strip();

export type GroupRegistrationSettingsInput = z.infer<
  typeof GroupRegistrationSettingsInputValidationSchema
>;

const attendeeDetailsApiFields = {
  enabled: z.boolean().meta({
    description:
      'Collect a name, an email address and the answers to any attendee questions for each ticket. Defaults to `false`.',
  }),
  when: z.enum(GROUP_REGISTRATION_WHEN).meta({
    description:
      '`checkout`: the customer adds attendees before paying. `afterBooking`: they add them after booking, from the confirmation page, their email or the customer portal. Defaults to `checkout`.',
  }),
  requireAllBeforePayment: z.boolean().meta({
    description:
      'With `when: checkout`, every attendee must be added before the customer can pay. Defaults to `true`.',
  }),
  emailDomains: z.enum(GROUP_REGISTRATION_EMAIL_DOMAINS).meta({
    description:
      "Which attendee email addresses are accepted. `any` (default): any address. `flag`: any, with personal addresses (Gmail, Outlook and the like) flagged for review. `work`: work addresses only. `workFlag`: work addresses only, with ones outside the booker's company flagged for review. `same`: addresses at the booker's company only.",
  }),
  sendReminders: z.boolean().meta({
    description: 'Email the booker reminders until every attendee is added. Defaults to `true`.',
  }),
  heading: z.string().max(200).meta({ description: 'Heading above the attendee rows.' }),
  description: z
    .string()
    .max(GUEST_DESCRIPTION_MAX_LENGTH)
    .meta({ description: 'Text under the heading.' }),
  saveButton: z
    .string()
    .max(GUEST_SAVE_BUTTON_MAX_LENGTH)
    .meta({ description: "Label of each attendee row's save button." }),
};

export const AttendeeDetailsApiInputValidationSchema = z
  .object(attendeeDetailsApiFields)
  .partial()
  .meta({
    description:
      'Attendee details for this event: whether a name and email address are collected for each ticket, when, and which email addresses are accepted. The attendee questions start as Name and Email address; add more in the dashboard.',
  });

export const AttendeeDetailsApiResponseValidationSchema = z
  .object({
    ...attendeeDetailsApiFields,
    enabledAt: z.string().meta({
      description:
        'ISO 8601 timestamp of when attendee details were first switched on. Bookings made before it get no automatic reminders.',
    }),
  })
  .partial()
  .meta({ description: 'Attendee details settings for this event.' });
